import {
  AwayTeamComponent,
  HomeTeamComponent,
  IconsResourceComponent,
  StatusMatchComponent,
  VsScoresComponent,
} from '@/app/components/shared/match-by-competition';
import {
  EStatusMatches,
  MatchApi,
} from '@/app/pages/sports/football/home/models';
import { CalendarService } from '@/app/services/calendar.service';
import { TranslationService } from '@/app/services/translation.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { WebsocketFavortiesService } from '../../services';
import { ELanguage } from '@/app/utils';
import { ROUTE_FOOTBALL_MATCHS, ROUTE_FOOTBALL_MATCHS_EN } from '@/app/routes';

@Component({
  selector: 'app-match-favorite-item',
  standalone: true,
  imports: [
    HomeTeamComponent,
    RouterLink,
    VsScoresComponent,
    AwayTeamComponent,
    StatusMatchComponent,
    IconsResourceComponent,
  ],
  templateUrl: './match-favorite-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatchFavoriteItemComponent {
  // ✅ Servicios
  private translationService = inject(TranslationService);
  private calendarService = inject(CalendarService);
  private websocketFavortiesService = inject(WebsocketFavortiesService);

  // ✅ Constantes
  readonly urlIcon = environment.urlIcon;
  readonly imageDefault = `${environment.urlIMG}default.png`;
  noPro = environment.envName === 'local' || environment.envName === 'dev';

  data = input.required<MatchApi>();

  readonly selectedDate = computed(() =>
    this.calendarService.selectedDate().replaceAll('-', '')
  );

  // ✅ Efecto para actualizar liveDataMap si llega ws
  private syncLiveData = effect(
    () => {
      const id = this.data().Id;
      const ws = this.websocketFavortiesService.wsMatchesData()[id];

      if (ws) {
        this.websocketFavortiesService.liveDataMap.update((prev) => ({
          ...prev,
          [id]: {
            AwayHalfScore: ws.halfAwayScore,
            AwayScore: ws.awayScore,
            GamePeriod: ws.gamePeriod,
            Status: ws.state,
            HomeHalfScore: ws.halfHomeScore,
            HomeScore: ws.homeScore,
            GameTimeAdd: ws.gameTimeAdd,
            AwayPenaltyScore: ws.penaltyAwayScore,
            HomePenaltyScore: ws.penaltyHomeScore,
            PeriodStartTime:
              ws.periodStartTime !== null
                ? ws.periodStartTime.toISOString()
                : this.data().PeriodStartTime,
          },
        }));

        // this.hasWs.set(true);
      }
    },
    { allowSignalWrites: true }
  );

  liveMatch = computed(() => {
    const base = this.data();
    const liveMatch = this.websocketFavortiesService.liveDataMap()[base.Id];

    return {
      ...base,
      ...liveMatch,
    };
  });

  getUrlForMatches(): string {
    const slug = this.translationService.slugifyNameRoute(
      {
        NameTranslation: this.liveMatch().HomeNameTranslation,
        defaultName: this.liveMatch().HomeName,
      },
      {
        NameTranslation: this.liveMatch().AwayNameTranslation,
        defaultName: this.liveMatch().AwayName,
      }
    );

    if (this.translationService.getLangLocalStorage() === ELanguage.EN) {
      return `/${ROUTE_FOOTBALL_MATCHS_EN.replace(':slug', slug).replace(
        ':matchId',
        this.liveMatch().Id
      )}`;
    } else {
      return `/${ROUTE_FOOTBALL_MATCHS.replace(':slug', slug).replace(
        ':matchId',
        this.liveMatch().Id
      )}`;
    }
  }

  getUrlMatch(): string {
    const lang = this.translationService.getLangLocalStorage();
    const sport = lang === 'es' ? 'futbol' : 'football';
    const match = lang === 'es' ? 'partido' : 'match';

    const slug = this.translationService.slugifyNameRoute(
      {
        NameTranslation: this.liveMatch().HomeNameTranslation,
        defaultName: this.liveMatch().HomeName,
      },
      {
        NameTranslation: this.liveMatch().AwayNameTranslation,
        defaultName: this.liveMatch().AwayName,
      }
    );

    return `${sport}/${match}/${slug}/${this.liveMatch().Id}`;
  }

  competitionCountryName() {
    return '';
  }

  homeTeamData() {
    const data = this.liveMatch();
    return {
      id: data.HomeId,
      name: data.HomeName,
      logo: data.HomeLogo || this.imageDefault,
      score: data.HomeScore,
      halfScore: data.HomeHalfScore,
      penaltyScore: data.HomePenaltyScore,
      nameTranslation: data.HomeNameTranslation,
      tsExternalId: data.HomeTsExternalId,
    };
  }

  awayTeamData() {
    const data = this.liveMatch();
    return {
      id: data.AwayId,
      name: data.AwayName,
      logo: data.AwayLogo || this.imageDefault,
      score: data.AwayScore,
      halfScore: data.AwayHalfScore,
      penaltyScore: data.AwayPenaltyScore,
      nameTranslation: data.AwayNameTranslation,
      tsExternalId: data.AwayTsExternalId,
    };
  }

  winHomeTeam(): boolean {
    const m = this.liveMatch();
    return m.Status === EStatusMatches.ENDED && m.HomeScore > m.AwayScore;
  }

  winAwayTeam(): boolean {
    const m = this.liveMatch();
    return m.Status === EStatusMatches.ENDED && m.AwayScore > m.HomeScore;
  }
}
